===================
Letstream Select Date Component (with Range Support)
Author: @letstream

Events: 
 - @error
 - @success

Props:
 - selectedDate (Required for single): State to save given input
 - startDate (Required for range): State to save start date
 - endDate (Required for range): State to save end date
 - isRange (Optional): Boolean to enable range selection (default: false)
 - label (Required)
 - successText (Required): Text message for correct input
 - dangerText (Required): Text message for incorrect input

====================

<template>
    <div :class="position">
        <label class="vs-input--label" v-if="label">{{label}}</label>
        
        <datepicker 
            v-if="!isRange"
            placeholder="DD MMM YYYY"
            :class="classAnother" 
            v-model="value"
            :format="getFormattedDate"
            :disabled-dates="disabled_dates"
            :typeable="true"
            :clear-button="true"
            :disabled="disabled"
        ></datepicker>

        <div v-else class="range-picker-wrapper">
            <datepicker 
                placeholder="Start Date"
                :class="[classAnother, 'range-start']" 
                v-model="startDateValue"
                :format="getFormattedDate"
                :disabled-dates="disabled_dates"
                :typeable="true"
                :clear-button="true"
                :disabled="disabled"
            ></datepicker>
            <span class="range-separator">to</span>
            <datepicker 
                placeholder="End Date"
                :class="[classAnother, 'range-end']" 
                v-model="endDateValue"
                :format="getFormattedDate"
                :disabled-dates="endDisabledDates"
                :typeable="true"
                :clear-button="true"
                :disabled="disabled || !startDateValue"
            ></datepicker>
        </div>

        <span style="color:red" v-if="warning">{{warning_text}}</span>
    </div>
</template>

<script>
import Datepicker from 'vuejs-datepicker';
import moment from 'moment';

export default {  
    props: {
        placeHolder: {
            type : String,
            default : "",
        },
        label: {
            type : String,
            default : "",
        },
        successText : {
            type: String,
            default: '',
        },
        dangerText : {
            type: String,
            default: '',
        },
        classAnother : {
            type: String,
            default: '',
        },
        warning : {
            type: Boolean,
            default: false
        },
        warning_text : {
            default: null
        },
        isRange: {
            type: Boolean,
            default: false
        },
        selectedDate: {
            default: null
        },
        startDate: {
            default: null
        },
        endDate: {
            default: null
        },
        minimum_date: {
            default: null
        },
        position: {
            default: "bottom"
        },
        disabled: {
            type: Boolean,
            default: false
        },
        allow_all_dates: {
            type: Boolean,
            default: false
        },
        allow_future_dates: {
            type: Boolean,
            default: false
        }
    },
    components: {
        Datepicker
    },
    data() {
        let today = new Date(Date.now())

        return {
            is_success : null,
            value: null,
            startDateValue: null,
            endDateValue: null,
            is_invalid : null,
            disabled_dates: this.allow_all_dates ? {} : {
                from: this.allow_future_dates ? null : today,
                to: this.minimum_date
            }
        }
    },
    computed: {
        endDisabledDates() {
            if(this.allow_all_dates) return {}
            
            let dates = {
                from: this.allow_future_dates ? null : new Date(Date.now()),
                to: this.startDateValue || this.minimum_date
            }
            
            return dates
        }
    },
    mounted() {
        if(!this.isRange && this.selectedDate){
            if(typeof this.selectedDate == "string")
                this.value = new Date(this.selectedDate)
            else
                this.value = this.selectedDate
        }

        if(this.isRange){
            if(this.startDate){
                if(typeof this.startDate == "string")
                    this.startDateValue = new Date(this.startDate)
                else
                    this.startDateValue = this.startDate
            }
            if(this.endDate){
                if(typeof this.endDate == "string")
                    this.endDateValue = new Date(this.endDate)
                else
                    this.endDateValue = this.endDate
            }
        }

        if(this.minimum_date && !this.allow_all_dates){
            if(typeof this.minimum_date == "string")
                this.disabled_dates.to = new Date(this.minimum_date)
            else
                this.disabled_dates.to = this.minimum_date
        }
    },
    watch: {
        minimum_date: function(value, oldV){
            if(value == oldV) return
            if(this.allow_all_dates) return
            if(value){
                if(typeof value == "string")
                    this.disabled_dates.to = new Date(value)
                else
                    this.disabled_dates.to = value
            }
        },
        selectedDate: function (value, oldV) {
            if(value == oldV || this.isRange) return
            if(value){
                if(typeof value == "string")
                    this.value = new Date(value)
                else
                    this.value = value
            }
        },
        startDate: function (value, oldV) {
            if(value == oldV || !this.isRange) return
            if(value){
                if(typeof value == "string")
                    this.startDateValue = new Date(value)
                else
                    this.startDateValue = value
            }
        },
        endDate: function (value, oldV) {
            if(value == oldV || !this.isRange) return
            if(value){
                if(typeof value == "string")
                    this.endDateValue = new Date(value)
                else
                    this.endDateValue = value
            }
        },
        value: function (value, oldV) {
            if(value == oldV || this.isRange) return
            if(value){
                this.is_success = true
                this.is_invalid = false
                this.$emit('success', this.successText)
                this.$emit('update:selectedDate', this.formatDate(value))
            }else{
                this.is_success = false
                this.is_invalid = true
                this.$emit('error', this.dangerText)
                this.$emit('update:selectedDate', this.formatDate(value))
            }
        },
        startDateValue: function (value, oldV) {
            if(value == oldV || !this.isRange) return
            
            // Clear end date if it's before start date
            if(value && this.endDateValue && this.endDateValue < value){
                this.endDateValue = null
            }

            this.$emit('update:startDate', this.formatDate(value))
            this.checkRangeValidity()
        },
        endDateValue: function (value, oldV) {
            if(value == oldV || !this.isRange) return
            
            this.$emit('update:endDate', this.formatDate(value))
            this.checkRangeValidity()
        }
    },
    methods: {
        formatDate(date) {
            if(!date) return null
            let d = date
            if(typeof date == 'string')
                d = new Date(date)
            return moment(date).format('YYYY-MM-DD')
        },
        getFormattedDate(date) {
            return moment(date).format('DD MMM YYYY')
        },
        checkRangeValidity() {
            if(this.startDateValue && this.endDateValue){
                this.is_success = true
                this.is_invalid = false
                this.$emit('success', this.successText)
            } else if(!this.startDateValue && !this.endDateValue){
                this.is_success = false
                this.is_invalid = true
                this.$emit('error', this.dangerText)
            }
        }
    }
}
</script>

<style lang="scss">
    .vdp-datepicker__clear-button {
        position: absolute;
        right: 10px;
        top: 0;
        bottom: 0;
        margin-top: auto;
        margin-bottom: auto;
        height: 1.5em;
        font-size: 1.5em;
    }
    .top .vdp-datepicker__calendar  {
        bottom: 100%;
    }
    
    .range-picker-wrapper {
        display: flex;
        align-items: center;
        gap: 10px;
        flex-wrap: wrap;

        .range-start,
        .range-end {
            flex: 1;
            min-width: 150px;
        }

        .range-separator {
            color: #666;
            font-weight: 500;
            padding: 0 5px;
        }
    }
</style>